<template>
  <div class="animated fadeIn">
    <b-row class="py-2">
      <b-card header="Editar Campaña" class="w-100">
        <b-form @submit.prevent="store" enctype="multipart/form-data">

          <b-row class="px-3">

            <b-form-group label="<strong>Nombre *</strong>" label-for="name" class="w-100">
              <b-form-input id="name" v-model="campaign.name" required />
            </b-form-group>

            <b-form-group label="<strong>Fecha de envío</strong>" label-for="publishedAt" class="w-100">
              <vue2-date-picker
                v-model="campaign.publishedAt"
                type="datetime"
                :format="`DD/MM/YYYY HH:mm`"
                :time-picker-options="timePickerOptions"
                :lang="lang"
                :editable="false"
                :first-day-of-week="1"
                :disabled="!!limitForm"
                class="w-100"
              />
            </b-form-group>

            <b-form-group label="<strong>Nombre de envío</strong>" class="w-100">
              <b-form-input v-model="campaign.senderName" />
            </b-form-group>

            <b-form-group label="<strong>Email de envío</strong>" class="w-100"
              :state="getEmailValidationState()"
              :invalid-feedback="getEmailErrorMessage()"
            >
              <b-form-input v-model="campaign.sender" type="email" />
            </b-form-group>

            <b-form-group label="<strong>Email de respuesta</strong>" class="w-100"
              :state="getResponseEmailValidationState()"
              :invalid-feedback="getResponseEmailErrorMessage()"
            >
              <b-form-input v-model="campaign.responder" type="email" />
            </b-form-group>

            <b-form-group label="<strong>Emails adicionales</strong>" class="w-100"
              :state="getEmailsValidationState()"
              :invalid-feedback="getEmailsErrorMessage()"
            >
              <b-form-textarea
                v-model="emailsText"
                placeholder="Introduce emails adicionales separados por coma o línea nueva"
                rows="3"
                @input="updateEmails"
              ></b-form-textarea>
              <small class="text-muted">Puedes añadir múltiples emails separados por comas o líneas nuevas</small>
            </b-form-group>

            <b-form-group label="<strong>Tipo</strong>" class="w-100">
              <multiselect
                v-model="campaign.campaignTypes"
                :options="campaignTypes"
                track-by="uuid"
                label="name"
                :multiple="true"
              />
            </b-form-group>

            <b-form-group label="<strong>Lista de Distribución</strong>" class="w-100">
              <multiselect
                v-model="campaign.distributionLists"
                :options="distributionLists"
                track-by="uuid"
                label="name"
                :multiple="true"
              />
            </b-form-group>

            <!-- Editor selector -->
            <b-form-group label="<strong>Editor de plantilla</strong>" class="w-100">
              <div class="editor-toggle">
                <button
                  type="button"
                  :class="['editor-toggle-btn', selectedEditor === 'ckeditor' ? 'active' : '']"
                  @click="switchEditor('ckeditor')"
                >
                  <i class="fa fa-code mr-1"></i> Editor Clásico
                </button>
                <button
                  type="button"
                  :class="['editor-toggle-btn', selectedEditor === 'grapes' ? 'active' : '']"
                  @click="switchEditor('grapes')"
                >
                  <i class="fa fa-th-large mr-1"></i> Editor por Bloques
                </button>
              </div>
            </b-form-group>

            <!-- Plantilla (Cambio estratégico a v-if) -->
            <b-form-group class="w-100">
              <template #label>
                <strong>Plantilla de campaña *</strong>
              </template>

              <!-- GrapesJS -->
              <div v-if="selectedEditor === 'grapes'">
                <div id="gjs" style="height: 700px; border: 1px solid #ddd; background: #fff;"></div>
              </div>

              <!-- CKEditor -->
              <div v-if="selectedEditor === 'ckeditor'">
                <ckeditor
                  :editor="editor"
                  id="editor"
                  name="html"
                  v-model="campaign.html"
                  :config="editorConfig"
                ></ckeditor>
              </div>
            </b-form-group>

          </b-row>

          <!-- Adjuntos -->
          <b-row :class="{ 'disabled-row': campaign.status !== 'Pending' }">
            <b-col>
              <b-form-group label="<strong>Ficheros adjuntos</strong>">
                <b-form-file
                  v-model="campaign.attachments"
                  :multiple="true"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Existing attachments -->
          <b-row v-if="campaign.existingAttachments && campaign.existingAttachments.length > 0" class="mb-3">
            <b-col
              v-for="attachment in campaign.existingAttachments"
              :key="attachment.uuid"
            >
              <a @click="getAttachmentUrl(attachment.uuid)">
                {{ attachment.name }}
              </a>
              <a
                v-if="campaign.status === 'Pending'"
                @click="deleteAttachment(attachment.uuid)"
              >
                Borrar
              </a>
            </b-col>
          </b-row>

          <!-- Submit -->
          <b-row>
            <div class="col">
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="!requirements()"
              >
                Guardar
              </button>
            </div>
          </b-row>

        </b-form>
      </b-card>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios';
import { request } from '@/helpers/services';
import Multiselect from 'vue-multiselect';
import Vue2DatePicker from 'vue2-datepicker';
import { getDistributions } from "@/api/distribution-list-api";
import { getCampaignTypes } from "@/api/campaign-type-api";
import { getAttachmentPreview } from "@/api/attachment-api";
import { deleteCampaignAttachment } from "@/api/campaign-api";
import { normalizeCampaignHtml } from '@/utils/campaignHtmlAdapter';

// GrapesJS
import grapesjs from "grapesjs/dist/grapes.min.js";
import "grapesjs/dist/css/grapes.min.css";
import newsletter from "grapesjs-preset-newsletter";

// CKEditor
import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import { Bold, Code, Italic, Strikethrough, Subscript, Superscript, Underline } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace';
import { Font } from '@ckeditor/ckeditor5-font';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Highlight } from '@ckeditor/ckeditor5-highlight';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import { AutoImage, Image, ImageCaption, ImageInsert, ImageResize, ImageStyle, ImageToolbar } from '@ckeditor/ckeditor5-image';
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent';
import { AutoLink, Link, LinkImage } from '@ckeditor/ckeditor5-link';
import { List, ListProperties, TodoList } from '@ckeditor/ckeditor5-list';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { Mention } from '@ckeditor/ckeditor5-mention';
import { PageBreak } from '@ckeditor/ckeditor5-page-break';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { ShowBlocks } from '@ckeditor/ckeditor5-show-blocks';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import { SpecialCharacters, SpecialCharactersEssentials } from '@ckeditor/ckeditor5-special-characters';
import { WordCount } from '@ckeditor/ckeditor5-word-count';
import { ImportWord } from '@ckeditor/ckeditor5-import-word';
import { ExportPdf } from '@ckeditor/ckeditor5-export-pdf';
import { ExportWord } from '@ckeditor/ckeditor5-export-word';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties, TableToolbar } from '@ckeditor/ckeditor5-table';

export default {
  name: 'editCampaigns',
  components: { Multiselect, Vue2DatePicker },

  data() {
    return {
      selectedEditor: 'ckeditor',
      gjs: null,
      uuid: null,

      // CKEditor
      editor: ClassicEditor,
      editorConfig: {
        plugins: [
          Autoformat, BlockQuote, Bold, Heading, Image, ImageCaption,
          ImageStyle, ImageToolbar, Indent, Italic, Link, List, MediaEmbed,
          Paragraph, Table, TableToolbar, Alignment, AutoImage, AutoLink,
          Code, CodeBlock, Essentials,
          FindAndReplace, Font, Highlight, HorizontalLine,
          HtmlEmbed, ImageInsert, ImageResize, IndentBlock,
          LinkImage, ListProperties, TodoList, Mention, PageBreak, PasteFromOffice,
          RemoveFormat, ShowBlocks, SourceEditing,
          SpecialCharacters, SpecialCharactersEssentials,
          Underline, WordCount,
          CloudServices, ExportPdf, ExportWord, ImportWord,
          TableCaption, TableCellProperties, TableColumnResize,
          TableProperties,
          Strikethrough, Subscript, Superscript,
        ],
        toolbar: {
          items: [
            'undo', 'redo', '|',
            'exportPdf', 'exportWord', 'importWord', '|',
            'showBlocks', 'findAndReplace', 'selectAll', '|',
            'heading', '|',
            'fontSize', 'fontFamily',
            { label: 'Font color', icon: 'plus', items: ['fontColor', 'fontBackgroundColor'] },
            '|', '-',
            'bold', 'italic', 'underline',
            { label: 'Formatting', icon: 'text', items: ['strikethrough', 'subscript', 'superscript', 'code', 'horizontalLine', '|', 'removeFormat'] },
            'specialCharacters', 'pageBreak', '|',
            'link', 'insertImage', 'insertTable',
            { label: 'Insert', icon: 'plus', items: ['highlight', 'blockQuote', 'mediaEmbed', 'codeBlock', 'htmlEmbed'] },
            '|', 'alignment', '|',
            'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent', '|',
            'sourceEditing'
          ],
          shouldNotGroupWhenFull: true
        },
        language: 'en',
        image: {
          styles: ['alignCenter', 'alignLeft', 'alignRight'],
          resizeOptions: [
            { name: 'resizeImage:original', label: 'Original', value: null },
            { name: 'resizeImage:50', label: '50%', value: '50' },
            { name: 'resizeImage:75', label: '75%', value: '75' }
          ],
          toolbar: ['imageTextAlternative', 'toggleImageCaption', '|', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|', 'resizeImage']
        },
        imageInsert: {
          integrations: ['insertImageViaUrl']
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties', 'toggleTableCaption']
        },
        htmlSupport: {
          allow: [{ name: /^.*$/, styles: true, attributes: true, classes: true }]
        },
        fontFamily: { supportAllValues: true },
        fontSize: { options: [10, 12, 14, 'default', 18, 20, 22], supportAllValues: true },
        htmlEmbed: { showPreviews: true },
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        list: { properties: { styles: true, startIndex: true, reversed: true } },
        link: {
          decorators: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            toggleDownloadable: { mode: 'manual', label: 'Downloadable', attributes: { download: 'file' } }
          }
        }
      },

      campaign: {
        name: '',
        publishedAt: null,
        senderName: '',
        sender: '',
        responder: '',
        html: '',
        distributionLists: [],
        campaignTypes: [],
        attachments: [],
        existingAttachments: [],
        emails: []
      },
      emailsText: '',

      limitForm: false,
      distributionLists: [],
      campaignTypes: [],

      timePickerOptions: {
        start: '00:00',
        step: '00:05',
        end: '23:55'
      },

      lang: {
        days: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        pickers: ['next 7 days', 'next 30 days', 'previous 7 days', 'previous 30 days'],
        placeholder: { date: 'Seleccione fecha y hora' }
      },

      emailValidation: null,
      responseEmailValidation: null,
      emailsValidation: null,
      typeOfTemplate: null
    };
  },

  async mounted() {
    this.$eventHub.$emit('captureMenu', [
      { name: 'Volver', path: '/communications/campaign/list', brand: 'fa fa-arrow-left' }
    ]);

    this.uuid = this.$route.params.uuid;
    this.typeOfTemplate = this.$route.query.typeOfTemplate;

    await this.getCampaign();
    await this.getDistributions();
    await this.getCampaignTypes();

    if (this.selectedEditor === 'grapes') {
      this.$nextTick(() => this.initGrapes());
    }
  },

  methods: {
    switchEditor(editor) {
      if (this.selectedEditor === editor) return;

      let currentHtml = '';

      if (this.selectedEditor === 'grapes') {
        currentHtml = this.getGrapesHtml();
        if (this.gjs) {
          this.gjs.destroy();
          this.gjs = null;
        }
        this._gjsOriginalHtml = null;
        this._gjsUserEdited = false;
        this._gjsReady = false;
      } else if (this.selectedEditor === 'ckeditor') {
        const cleanSnippetHtml = this.unwrapAllHtmlSnippets(this.campaign.html);
        currentHtml = this.unscopeHtmlStyles(cleanSnippetHtml);
      }

      this.selectedEditor = editor;

      if (editor === 'ckeditor') {
        const scopedHtml = this.scopeHtmlStyles(currentHtml);
        this.campaign.html = `<div class="raw-html-embed">${scopedHtml}</div>`;
      } else if (editor === 'grapes') {
        this.campaign.html = currentHtml;
        this.$nextTick(() => {
          this.initGrapes(currentHtml);
        });
      }
    },

    initGrapes(htmlOverride) {
      if (this.gjs) {
        this.gjs.destroy();
        this.gjs = null;
      }

      const htmlToLoad = normalizeCampaignHtml(htmlOverride || this.campaign.html);
      this._gjsOriginalHtml = htmlToLoad;
      this._gjsUserEdited = false;
      this._gjsReady = false;

      this.gjs = grapesjs.init({
        container: '#gjs',
        height: '700px',
        fromElement: false,
        storageManager: false,
        protectedCss: '',

        assetManager: {
          upload: false,
          showUrlInput: true,
        },

        plugins: [newsletter],
        pluginsOpts: { [newsletter]: {} },

        domComponents: { 
          storeWrapper: false 
        },

        components: htmlToLoad || '',

        canvas: {
          styles: [
            "https://stackpath.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css"
          ]
        }
      });

      this.gjs.on('load', () => {
        this._gjsReady = true;
      });

      this.gjs.on('update', () => {
        if (!this._gjsReady) return;
        this._gjsUserEdited = true;
      });
    },

    unwrapAllHtmlSnippets(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      
      const snippets = div.querySelectorAll('.raw-html-embed');
      snippets.forEach(snippet => {
        const parent = snippet.parentNode;
        if (parent) {
          while (snippet.firstChild) {
            parent.insertBefore(snippet.firstChild, snippet);
          }
          parent.removeChild(snippet);
        }
      });
      return div.innerHTML;
    },

    scopeHtmlStyles(html) {
      if (!html) return '';
      return html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => {
        const scopedCss = css.replace(/([^\r\n,{}]+)(?=\s*\{)/g, (selectorMatch) => {
          let cleanSelector = selectorMatch.trim();
          if (cleanSelector.startsWith('@') || cleanSelector.startsWith('from') || cleanSelector.startsWith('to') || /^\d+%/.test(cleanSelector)) {
            return selectorMatch;
          }
          return cleanSelector.split(',').map(sel => {
            let s = sel.trim();
            if (s === 'html' || s === 'body') return '.raw-html-embed';
            if (s.startsWith('html') || s.startsWith('body')) return '.raw-html-embed' + s.replace(/^(html|body)/, '');
            return '.raw-html-embed ' + s;
          }).join(', ');
        });
        return `<style>${scopedCss}</style>`;
      });
    },

    unscopeHtmlStyles(html) {
      if (!html) return '';
      return html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => {
        const unscopedCss = css.replace(/([^\r\n,{}]+)(?=\s*\{)/g, (selectorMatch) => {
          return selectorMatch.split(',').map(sel => {
            let s = sel.trim();
            if (s === '.raw-html-embed') return 'html, body';
            if (s.startsWith('.raw-html-embed ')) {
              return s.replace(/^\.raw-html-embed\s+/, '');
            }
            return s;
          }).join(', ');
        });
        return `<style>${unscopedCss}</style>`;
      });
    },

    // ─── Validations ──────────────────────────────────────────────────────────

    validateEmail() {
      this.emailValidation = /^[a-zA-Z0-9._%+-]+@ata\.es$/.test(this.campaign.sender);
    },
    validateResponseEmail() {
      this.responseEmailValidation = /^[a-zA-Z0-9._%+-]+@ata\.es$/.test(this.campaign.responder);
    },
    getEmailValidationState() { return this.emailValidation; },
    getResponseEmailValidationState() { return this.responseEmailValidation; },
    getEmailErrorMessage(){
      if (this.campaign.sender && this.emailValidation === false) {
        return 'Introduce un email válido del dominio ata.es';
      }
      return null;
    }, 
    getResponseEmailErrorMessage() {
      if (this.campaign.responder && this.responseEmailValidation === false) {
        return 'Introduce un email válido del dominio ata.es';
      }
      return null;
    },

    updateEmails() {
      const emails = this.emailsText
        .split(/[, \r\n]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0);
      
      this.campaign.emails = emails;
      this.validateEmails();
    },
    
    validateEmails() {
      if (!this.campaign.emails || this.campaign.emails.length === 0) {
        this.emailsValidation = null;
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const allValid = this.campaign.emails.every(email => emailRegex.test(email));
      this.emailsValidation = allValid;
    },
    
    getEmailsValidationState() {
      return this.emailsValidation;
    },
    
    getEmailsErrorMessage() {
      if (this.emailsValidation === false) {
        return 'Uno o más emails no son válidos';
      }
      return null;
    },

    // ─── API ──────────────────────────────────────────────────────────────────

    async getCampaign() {
      try {
        this.$_loadingMixin_showLoading();
        const data = await request('campaign/' + this.uuid, 'get', 200);
        
        let rawHtml = data.html || '';

        if (this.selectedEditor === 'ckeditor' && rawHtml) {
          const scopedHtml = this.scopeHtmlStyles(rawHtml);
          rawHtml = `<div class="raw-html-embed">${scopedHtml}</div>`;
        }

        this.campaign = {
          ...data,
          html: rawHtml,
          existingAttachments: (data.attachments || []).map(a => ({
            name: a.name,
            uuid: a.uuid
          })),
          attachments: [],
          emails: data.emails || []
        };
        
        if (this.campaign.emails && this.campaign.emails.length > 0) {
          this.emailsText = this.campaign.emails.join(', ');
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.$_loadingMixin_hideLoading();
      }
    },

    getGrapesHtml() {
      if (!this._gjsUserEdited) {
        return this._gjsOriginalHtml || '';
      }
      const inlineHtml = this.gjs.runCommand('gjs-get-inlined-html');
      return inlineHtml || '';
    },

    async getDistributions() {
      try {
        const res = await getDistributions();
        this.distributionLists = res.distributionLists;
      } catch (e) {}
    },

    async getCampaignTypes() {
      try {
        const res = await getCampaignTypes();
        this.campaignTypes = res.campaignTypes;
      } catch (e) {}
    },

    async getAttachmentUrl(uuid) {
      try {
        this.$_loadingMixin_showLoading();
        const blob = await getAttachmentPreview(uuid);
        window.open(URL.createObjectURL(blob));
      } catch (e) {
        this.$_notificationMixin_error("Error", "No hemos podido recuperar la previsualización");
      } finally {
        this.$_loadingMixin_hideLoading();
      }
    },

    deleteAttachment(uuid) {
      swal({
        title: '¿Desea eliminar el documento?',
        text: "Esta operación no se podrá revertir",
        type: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Confirmar'
      }).then(async (res) => {
        if (!res.value) return;
        try {
          this.$_loadingMixin_showLoading();
          await deleteCampaignAttachment(this.campaign.uuid, uuid);
          this.$_notificationMixin_success('OK', 'Documento eliminado');
          this.getCampaign();
        } catch (e) {
          this.$_notificationMixin_error('Error', 'No se ha eliminado el documento');
        } finally {
          this.$_loadingMixin_hideLoading();
        }
      });
    },

    async store() {
      try {
        this.$_loadingMixin_showLoading();

        let finalHtml = '';
        if (this.selectedEditor === 'grapes' && this.gjs) {
          finalHtml = this.getGrapesHtml();
        } else {
          const cleanSnippetHtml = this.unwrapAllHtmlSnippets(this.campaign.html);
          finalHtml = this.unscopeHtmlStyles(cleanSnippetHtml);
        }

        const formData = new FormData();
        formData.append('name',        this.campaign.name);
        formData.append('html',        finalHtml || '');
        formData.append('publishedAt', this.campaign.publishedAt || '');
        formData.append('senderName',  this.campaign.senderName || '');
        formData.append('sender',      this.campaign.sender || '');
        formData.append('responder',   this.campaign.responder || '');

        (this.campaign.distributionLists || [])
          .forEach(d => formData.append('distributions[]', d.uuid));

        (this.campaign.campaignTypes || [])
          .forEach(c => formData.append('campaignTypes[]', c.uuid));

        (this.campaign.attachments || [])
          .forEach(file => formData.append('attachments[]', file));

        if (this.campaign.emails && this.campaign.emails.length > 0) {
          this.campaign.emails.forEach(email => formData.append('emails[]', email));
        } else {
          formData.append('emails', null);
        }

        const res = await axios.post(
          'campaign/' + this.uuid,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (res.status === 200) {
          this.$router.push('/communications/campaign/list');
        }

      } catch (e) {
        console.error(e);
      } finally {
        this.$_loadingMixin_hideLoading();
      }
    },

    requirements() {
      return !!(this.campaign.name && this.campaign.html);
    }
  },

  watch: {
    'campaign.sender':    'validateEmail',
    'campaign.responder': 'validateResponseEmail',
    'campaign.emails':    'validateEmails'
  },

  beforeDestroy() {
    if (this.gjs) {
      this.gjs.destroy();
      this.gjs = null;
    }
  }
};
</script>

<style>
.disabled-row {
  pointer-events: none;
  opacity: 0.5;
}

.gjs-am-file-uploader {
  display: none !important;
}

.ck.ck-file-dialog-button {
  display: none !important;
}

.editor-toggle {
  display: inline-flex;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.editor-toggle-btn {
  padding: 8px 18px;
  background: #f8f9fa;
  border: none;
  border-right: 1px solid #dee2e6;
  cursor: pointer;
  font-size: 14px;
  color: #495057;
  transition: background 0.15s, color 0.15s;
}

.editor-toggle-btn:last-child {
  border-right: none;
}

.editor-toggle-btn:hover {
  background: #e9ecef;
}

.editor-toggle-btn.active {
  background: #025542;
  color: #fff;
}
</style>